import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Users, AlertTriangle, CheckCircle, Loader2, FileCheck, RefreshCw, ShieldCheck, ShieldX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getApiUrl, getServerName, getSystemID, authFetch } from '../../config/api';
import { readApiAccessMessage } from '../../utils/apiAccessError';
// Removed PageHeader import


function PatronDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        setLoading(true);
        const response = await authFetch(getApiUrl('GET_RX_PROFILES_SUMMARY'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ServerName: getServerName(),
            SystemID: getSystemID(),
            Purpose: "GETRXPROFILESUMMARY",
            Source: "RXPROFILEWEB",
            DeviceType: "RXPROFILEWEB"
          })
        });
        
        if (!response.ok) {
          if (response.status === 403 || response.status === 401) {
            throw new Error(await readApiAccessMessage(response))
          }
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.ResponseCode !== "0") {
          throw new Error(`API returned error: ${data.ResponseMessage}`);
        }
        
        setSummaryData(data.OutputData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSummaryData();
  }, []); // Only run once on component mount

  // Helper function to safely parse number from string
  const safeParseInt = (value) => {
    if (!value || value === 'NA' || value === '') return 0;
    const parsed = parseInt(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to get dashboard data from API response
  const getDashboardData = () => {
    if (!summaryData) {
      return {
        profileStats: {
          total: 0,
          kycDone: 0,
          amlClear: 0,
          pendingApproval: 0,
          pepFlagged: 0,
          highRisk: 0,
          kycAndAmlDone: 0,
        },
        profilesCreated: {
          ytd: { current: 0, previous: 0 },
          mtd: { current: 0, previous: 0 }
        },
        profilesAccessed: {
          ytd: { current: 0, previous: 0 },
          mtd: { current: 0, previous: 0 }
        },
        verification: {
          dvsVerified: 0,
          amlClear: 0,
          amlPending: 0,
          amlRejected: 0,
          kycAndAmlDone: 0,
          pepFlagged: 0,
        }
      };
    }

    return {
      profileStats: {
        total: safeParseInt(summaryData.TotalProfilesCount),
        kycDone: safeParseInt(summaryData.KycDoneCount),
        amlClear: safeParseInt(summaryData.AmlPepClearCount),
        pendingApproval: safeParseInt(summaryData.PendingApprovalCount),
        pepFlagged: safeParseInt(summaryData.PepFlaggedCount),
        highRisk: safeParseInt(summaryData.HighRiskCount),
        kycAndAmlDone: safeParseInt(summaryData.KycAndAmlDoneCount),
      },
      profilesCreated: {
        ytd: { 
          current: safeParseInt(summaryData.CurrentYTDProfileCreatedCount), 
          previous: safeParseInt(summaryData.PrevYTDProfileCreatedCount) 
        },
        mtd: { 
          current: safeParseInt(summaryData.CurrentMTDProfileCreatedCount), 
          previous: safeParseInt(summaryData.PrevMTDProfileCreatedCount) 
        }
      },
      profilesAccessed: {
        ytd: { 
          current: safeParseInt(summaryData.CurrentYTDProfileAccessedCount), 
          previous: safeParseInt(summaryData.PrevYTDProfileAccessedCount) 
        },
        mtd: { 
          current: safeParseInt(summaryData.CurrentMTDProfileAccessedCount), 
          previous: safeParseInt(summaryData.PrevMTDProfileAccessedCount) 
        }
      },
      verification: {
        dvsVerified: safeParseInt(summaryData.DVS_Verified),
        amlClear: safeParseInt(summaryData.AmlPepClearCount || summaryData.AML_Approved),
        amlPending: safeParseInt(summaryData.AML_Pending),
        amlRejected: safeParseInt(summaryData.AML_Rejected),
        kycAndAmlDone: safeParseInt(summaryData.KycAndAmlDoneCount),
        pepFlagged: safeParseInt(summaryData.PepFlaggedCount),
      }
    };
  };

  const dashboardData = getDashboardData();

  const getPercentageChange = (current, previous) => {
    if (!previous) return current ? '100.0' : '0.0';
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };

  const renderMetricCard = (
    title, 
    current, 
    previous, 
    Icon, 
    color = 'blue'
  ) => {
    const change = getPercentageChange(current, previous);
    const isPositive = parseFloat(change) > 0;
    
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                <Icon className={`h-6 w-6 text-${color}-600`} />
              </div>
              <div className="ml-4">
                <CardDescription>{title}</CardDescription>
                <CardTitle className="text-2xl">{current.toLocaleString()}</CardTitle>
              </div>
            </div>
            <div className="text-right">
              <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? (
                  <TrendingUp className="h-4 w-4 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 mr-1" />
                )}
                <span className="text-sm font-medium">{change}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">vs Previous Period</p>
            </div>
          </div>
        </CardHeader>
        <CardFooter className="pt-2 border-t">
          <p className="text-sm text-muted-foreground">
            Previous: <span className="font-medium">{previous.toLocaleString()}</span>
          </p>
        </CardFooter>
      </Card>
    );
  };

  const renderSimpleCard = (title, value, Icon, color = 'blue') => (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
              <Icon className={`h-5 w-5 text-${color}-600`} />
            </div>
            <div className="ml-3">
              <CardDescription>{title}</CardDescription>
              <CardTitle className="text-xl">{value.toLocaleString()}</CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  )

  // Show loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-600" />
            <p className="text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state (but we fallback to mock data, so this rarely shows)
  if (error && !summaryData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
            <p className="text-red-600 mb-2">Access Denied</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* content begins directly under persistent title */}
      {/* removed demo/alert container when falling back to mock data */}

      {/* Profile Stats Overview — active patrons only */}
      <div className="mb-8">
        <div className="flex flex-wrap items-baseline gap-2 mb-4">
          <h3 className="text-xl font-semibold text-gray-800 m-0">Profile Statistics</h3>
          <span className="text-[11px] font-semibold bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded">
            Active only
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>Active Profiles</CardTitle>
              <CardDescription>Active members only (excludes archived / inactive)</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold">{dashboardData.profileStats.total.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>KYC done</CardTitle>
              <CardDescription>Active patrons with KYC completed / DVS verified</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-teal-700">{dashboardData.profileStats.kycDone.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {dashboardData.profileStats.total
                  ? ((dashboardData.profileStats.kycDone / dashboardData.profileStats.total) * 100).toFixed(1)
                  : '0.0'}
                % of active
              </p>
            </CardContent>
          </Card>

          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>AML / PEP clear</CardTitle>
              <CardDescription>Active patrons with latest screening status Clear</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-green-700">{dashboardData.profileStats.amlClear.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {dashboardData.profileStats.total
                  ? ((dashboardData.profileStats.amlClear / dashboardData.profileStats.total) * 100).toFixed(1)
                  : '0.0'}
                % of active
              </p>
            </CardContent>
          </Card>

          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>Pending approval</CardTitle>
              <CardDescription>Active patrons with membership pending approval</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-amber-700">{dashboardData.profileStats.pendingApproval.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {dashboardData.profileStats.total
                  ? ((dashboardData.profileStats.pendingApproval / dashboardData.profileStats.total) * 100).toFixed(1)
                  : '0.0'}
                % of active
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Year to Date Performance — active patrons only */}
      <div className="mb-8">
        <div className="flex flex-wrap items-baseline gap-2 mb-4">
          <h3 className="text-xl font-semibold text-gray-800 m-0">Year to Date Performance</h3>
          <span className="text-[11px] font-semibold bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded">
            Active only
          </span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          {renderMetricCard(
            'Profiles Created (YTD)',
            dashboardData.profilesCreated.ytd.current,
            dashboardData.profilesCreated.ytd.previous,
            Users,
            'blue'
          )}
          
          {renderMetricCard(
            'Profiles Accessed (YTD)',
            dashboardData.profilesAccessed.ytd.current,
            dashboardData.profilesAccessed.ytd.previous,
            BarChart3,
            'green'
          )}
        </div>
      </div>

      {/* Month to Date Performance */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Month to Date Performance</h3>
        <div className="grid grid-cols-2 gap-6">
          {renderMetricCard(
            'Profiles Created (MTD)',
            dashboardData.profilesCreated.mtd.current,
            dashboardData.profilesCreated.mtd.previous,
            Users,
            'purple'
          )}
          
          {renderMetricCard(
            'Profiles Accessed (MTD)',
            dashboardData.profilesAccessed.mtd.current,
            dashboardData.profilesAccessed.mtd.previous,
            BarChart3,
            'indigo'
          )}
        </div>
      </div>

      {/* Verification & Screening — wired from patron + verification_queue */}
      <div className="mb-8">
        <div className="flex flex-wrap items-baseline gap-2 mb-4">
          <h3 className="text-xl font-semibold text-gray-800 m-0">Verification & Screening</h3>
          <span className="text-[11px] font-semibold bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded">
            Active only
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {renderSimpleCard('KYC / DVS verified', dashboardData.verification.dvsVerified, FileCheck, 'teal')}
          {renderSimpleCard('AML / PEP clear', dashboardData.verification.amlClear, ShieldCheck, 'green')}
          {renderSimpleCard('KYC + AML done', dashboardData.verification.kycAndAmlDone, CheckCircle, 'blue')}
          {renderSimpleCard('AML pending', dashboardData.verification.amlPending, RefreshCw, 'amber')}
          {renderSimpleCard('AML rejected / hit', dashboardData.verification.amlRejected, ShieldX, 'red')}
          {renderSimpleCard('PEP flagged', dashboardData.verification.pepFlagged, AlertTriangle, 'orange')}
        </div>
      </div>

      {/* Daily Statistics */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Daily Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                Today's Profiles
              </CardTitle>
              <CardDescription>Profiles created today</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">
                {summaryData ? safeParseInt(summaryData.CurrentDTDProfileCreatedCount).toLocaleString() : '0'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                Yesterday's Profiles
              </CardTitle>
              <CardDescription>Profiles created yesterday</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">
                {summaryData ? safeParseInt(summaryData.PrevDTDProfileCreatedCount).toLocaleString() : '0'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-purple-600" />
                API Status
              </CardTitle>
              <CardDescription>Last data refresh</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-bold text-green-600">✓ Connected</p>
              <p className="text-sm text-gray-500 mt-1">
                {summaryData ? `Version 1.0.0.1` : 'No data'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PatronDashboard